import { zonedLocalToDate } from "@/features/calendar-events/utils/eventTime";
import { calendarDate, calendarDateKey } from "@/shared/utils/calendarDate";
import type { Availability, AvailabilityOverride, Schedule } from "../types/availabilityTypes";

export const scheduleWeekdays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;

type Scope = Pick<Availability, "userId" | "businessId" | "locationId">;

const inScope = (scope: Scope, value: Scope & { deletedAt: Date | null }) =>
  value.userId === scope.userId && value.businessId === scope.businessId &&
  value.locationId === scope.locationId && !value.deletedAt;

export function scopedSchedules(availabilities: Availability[], scope: Scope) {
  const schedules = availabilities.filter((value) => inScope(scope, value));
  return { schedules, active: schedules.find((value) => value.isActive) };
}

// Match booking precedence: time off wins, then the most recent date-specific hours.
export function overrideForDay(overrides: AvailabilityOverride[], scope: Scope, dateKey: string) {
  return overrides
    .filter((value) => inScope(scope, value) && value.isActive &&
      calendarDateKey(value.effectiveFrom) <= dateKey && calendarDateKey(value.effectiveTo) >= dateKey)
    .sort((a, b) => Number(b.overrideType === "unavailable") - Number(a.overrideType === "unavailable") ||
      b.effectiveFrom.getTime() - a.effectiveFrom.getTime() ||
      b.updatedAt.getTime() - a.updatedAt.getTime() ||
      b.createdAt.getTime() - a.createdAt.getTime() || a.id.localeCompare(b.id))[0];
}

export function weekdayIntervals(schedule: Schedule, dateKey: string) {
  return schedule[scheduleWeekdays[calendarDate(dateKey).getDay()]];
}

export function businessHoursForDay(
  availability: Availability | undefined,
  overrides: AvailabilityOverride[],
  dateKey: string,
): { startAt: Date; endAt: Date }[] {
  if (!availability) return [];
  const override = overrideForDay(overrides, availability, dateKey);
  if (override?.overrideType === "unavailable") return [];
  const intervals = override ? override.timeSlots : weekdayIntervals(availability.schedule, dateKey);
  return intervals.flatMap((interval) => {
    try {
      const startAt = zonedLocalToDate(`${dateKey}T${interval.start}`, availability.timezone);
      const endAt = zonedLocalToDate(`${dateKey}T${interval.end}`, availability.timezone);
      return endAt > startAt ? [{ startAt, endAt }] : [];
    } catch {
      return [];
    }
  }).sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
}
